import React, { useCallback, useMemo, useState } from 'react';

import { CallSplit, Cancel, CheckCircle, CheckCircleOutline, DeleteForever } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ListAltIcon from '@mui/icons-material/ListAlt';
import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  Divider,
  FormControlLabel,
  Paper,
  Radio,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import MappingLogModal from '~/components/Accommodation/Pages/Properties/components/MappingLogModal';
import VerificationStatusChip from '~/components/Common/VerificationStatusChip';

import useToggleState from '~/hooks/useToggleState';

import {
  ExtendedMappedInternalRoom,
  InternalRoom,
  MappedInternalRoom,
  Room,
  RoomMapping,
  RoomMappingPayload,
} from '~/services/AccommodationService';

import { titleCase } from '~/utils/stringUtils';

import SupplierRoomsForInternalRoomList from './SupplierRoomsForInternalRoomList';
import ToBeMappedRoomElement from './ToBeMappedRoomElement';

interface RoomWithPackage extends Room {
  hasActiveLpcPackage?: boolean;
}

type MappedRoomWithStatus = ExtendedMappedInternalRoom & {
  hasActiveLpcPackage?: boolean;
  hasApprovedLpcOffer?: boolean;
  hasBedbankRatesEnabled?: boolean;
};

interface Props {
  room: RoomWithPackage;
  onSelect: (room: RoomWithPackage) => void;
  isSelected: boolean;
  onRemoveMappedRoom: (room: ExtendedMappedInternalRoom) => void;
  onDecoupleMappedRoom: (room: ExtendedMappedInternalRoom) => void;
  onVerifyMappedRoom: (room: MappedInternalRoom) => void;
  clearNewMappings: () => void;
  removePayload: RoomMappingPayload;
  unsavedInternalRooms: Array<InternalRoom>;
  newMappings?: RoomMapping;
}

export default function ControlRoomElement({
  room,
  onSelect,
  isSelected,
  onRemoveMappedRoom,
  onDecoupleMappedRoom,
  onVerifyMappedRoom,
  clearNewMappings,
  unsavedInternalRooms,
  newMappings,
}: Props) {
  const [selectedMappedRoom, setSelectedMappedRoom] = useState<ExtendedMappedInternalRoom | null>(null);
  const [verifyMappedRoom, setVerifyMappedRoom] = useState<ExtendedMappedInternalRoom | null>(null);
  const [mappingLogsRoom, setMappingLogsRoom] = useState<MappedInternalRoom | null>(null);
  const [logsRoom, setLogsRoom] = useState<RoomWithPackage | null>(null);
  const {
    isToggled: isDeleteConfirmationModalOpen,
    toggleOn: setOpenDeleteConfirmationModal,
    toggleOff: setCloseDeleteConfirmationModal,
  } = useToggleState(false);

  const {
    isToggled: isVerifyConfirmationModalOpen,
    toggleOn: setOpenVerifyConfirmationModal,
    toggleOff: setCloseVerifyConfirmationModal,
  } = useToggleState(false);

  const {
    isToggled: isMappingLogModalOpen,
    toggleOn: setOpenMappingLogModal,
    toggleOff: setCloseMappingLogModal,
  } = useToggleState(false);

  const onRemoveExistingMappingsClick = useCallback(
    (mappedRoom: ExtendedMappedInternalRoom) => {
      setOpenDeleteConfirmationModal();
      setSelectedMappedRoom(mappedRoom);
    },
    [setOpenDeleteConfirmationModal],
  );

  const confirmMappedRoomDelete = useCallback(() => {
    onRemoveMappedRoom(selectedMappedRoom);
    setCloseDeleteConfirmationModal();
  }, [onRemoveMappedRoom, selectedMappedRoom, setCloseDeleteConfirmationModal]);

  const showConfirmVerifyRoomMapping = useCallback(
    (mappedRoom: ExtendedMappedInternalRoom) => {
      setVerifyMappedRoom(mappedRoom);
      setOpenVerifyConfirmationModal();
    },
    [setOpenVerifyConfirmationModal],
  );

  const confirmMappedRoomVerify = useCallback(async () => {
    if (verifyMappedRoom) {
      onVerifyMappedRoom(verifyMappedRoom);
      setCloseVerifyConfirmationModal();
    }
  }, [onVerifyMappedRoom, setCloseVerifyConfirmationModal, verifyMappedRoom]);

  const showMappingLogs = useCallback(
    async (room: RoomWithPackage, mappedRoom: MappedInternalRoom) => {
      setLogsRoom(room);
      setMappingLogsRoom(mappedRoom);
      setOpenMappingLogModal();
    },
    [setOpenMappingLogModal],
  );

  const hasBedbankMappings = room.mappedRooms.some((mr) => mr.internalService.toLowerCase() === 'bedbank');
  const hasReservationMappings = room.mappedRooms.some((mr) => mr.internalService.toLowerCase() === 'reservation');
  const bedbankVariantCount = room.mappedRooms.filter((mr) => mr.internalService.toLowerCase() === 'bedbank').length;

  const reservationChipProps = useMemo(() => {
    if (!hasReservationMappings) {
      return null;
    }
    const reservationRooms = room.mappedRooms.filter(
      (mr) => mr.internalService.toLowerCase() === 'reservation',
    ) as Array<MappedRoomWithStatus>;
    const hasBlue = reservationRooms.some((mr) => mr.hasActiveLpcPackage && mr.hasApprovedLpcOffer);
    const hasRed = reservationRooms.some((mr) => !mr.hasActiveLpcPackage);
    if (hasBlue) {
      return { color: 'primary' as const, variant: 'filled' as const };
    }
    if (hasRed) {
      return { color: 'error' as const, variant: 'filled' as const };
    }
    return { color: 'primary' as const, variant: 'outlined' as const };
  }, [hasReservationMappings, room.mappedRooms]);

  const bedbankChipProps = useMemo(() => {
    if (!hasBedbankMappings) {
      return null;
    }
    const bedbankRooms = room.mappedRooms.filter(
      (mr) => mr.internalService.toLowerCase() === 'bedbank',
    ) as Array<MappedRoomWithStatus>;
    const allValid = bedbankRooms.every((mr) => mr.hasBedbankRatesEnabled);
    return {
      color: allValid ? ('primary' as const) : ('error' as const),
      variant: 'filled' as const,
      title: allValid ? 'Bedbank rates enabled' : 'Bedbank rates are disabled for this room',
    };
  }, [hasBedbankMappings, room.mappedRooms]);

  const flattenedMappedRooms: Array<ExtendedMappedInternalRoom> = room.mappedRooms.map((mr) => ({
    mappedRoomId: mr.mappedRoomId,
    name: mr.name,
    internalRoomId: mr.internalRoomId,
    roomId: room.id,
    internalService: mr.internalService,
    internalServiceId: mr.internalServiceId,
    supplierRooms: mr.supplierRooms,
    verificationStatus: mr.verificationStatus,
  }));

  const [expandedInternalRoomIds, setExpandedInternalRoomIds] = useState<Set<string>>(
    () =>
      new Set(flattenedMappedRooms.filter((mr) => mr.verificationStatus !== 'Approved').map((mr) => mr.internalRoomId)),
  );

  const handleExpandedChange = useCallback((internalRoomId: string, isExpanded: boolean) => {
    setExpandedInternalRoomIds((prev) => {
      const next = new Set(prev);
      if (isExpanded) {
        next.add(internalRoomId);
      } else {
        next.delete(internalRoomId);
      }
      return next;
    });
  }, []);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        backgroundColor: !isSelected ? 'grey.100' : undefined,
        borderColor: isSelected ? 'info.light' : undefined,
      }}
    >
      <Stack direction="column" gap={1}>
        <Stack direction="row" justifyContent="space-between" width="100%" spacing={2} alignItems="center">
          <FormControlLabel
            checked={isSelected}
            onChange={() => onSelect(room)}
            control={<Radio />}
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6">{room.name}</Typography>
                {room.hasActiveLpcPackage !== undefined &&
                  (room.hasActiveLpcPackage ? (
                    <CheckCircle color="success" fontSize="small" />
                  ) : (
                    <Cancel color="error" fontSize="small" />
                  ))}
              </Stack>
            }
            componentsProps={{ typography: { variant: 'h6' } }}
          />

          <Stack direction="row" spacing={1} ml="auto">
            {bedbankVariantCount > 1 && (
              <Chip
                label={`${bedbankVariantCount} bedbank variants`}
                size="small"
                color="secondary"
                variant="outlined"
              />
            )}
            {hasBedbankMappings && bedbankChipProps && <Chip label="Bedbank" size="small" {...bedbankChipProps} />}
            {hasReservationMappings && reservationChipProps && (
              <Chip label="Reservation" size="small" {...reservationChipProps} />
            )}
          </Stack>
        </Stack>
        <Stack direction="column" gap={2}>
          <Stack direction="column">
            {flattenedMappedRooms.map((mappedRoom) => (
              <Accordion
                key={mappedRoom.internalRoomId}
                expanded={expandedInternalRoomIds.has(mappedRoom.internalRoomId)}
                onChange={(_e, isExpanded) => handleExpandedChange(mappedRoom.internalRoomId, isExpanded)}
                sx={{
                  borderColor: mappedRoom.verificationStatus === 'Approved' ? 'success.main' : 'warning.main',
                  borderWidth: '2px',
                  borderStyle: 'solid',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    minHeight: 72,
                    '& .MuiAccordionSummary-content': {
                      my: 2,
                    },
                  }}
                >
                  <Stack direction="row" gap={1}>
                    <VerificationStatusChip status={mappedRoom.verificationStatus} />
                    <span>
                      ({titleCase(mappedRoom.internalService)}) - {mappedRoom.name}
                      <br />
                      {mappedRoom.internalServiceId}
                    </span>
                  </Stack>
                  <Stack direction="row" gap={1} ml="auto" mr={1} onClick={(e) => e.stopPropagation()}>
                    {mappedRoom.internalService.toLowerCase() === 'bedbank' && (
                      <Tooltip title="Move this bedbank room into a new room of its own, saved immediately">
                        <span>
                          <Button
                            startIcon={<CallSplit />}
                            onClick={() => onDecoupleMappedRoom(mappedRoom)}
                            disabled={room.mappedRooms.filter((mr) => mr.verificationStatus !== 'Rejected').length < 2}
                          >
                            Decouple
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                    <Tooltip title="Remove Existing Mapping For Source Room">
                      <Button
                        startIcon={<DeleteForever />}
                        onClick={() => onRemoveExistingMappingsClick(mappedRoom)}
                        color="error"
                      >
                        Remove
                      </Button>
                    </Tooltip>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <SupplierRoomsForInternalRoomList
                    internalRoomId={mappedRoom.internalRoomId}
                    isExpanded={expandedInternalRoomIds.has(mappedRoom.internalRoomId)}
                    showNameInTitle
                  />
                </AccordionDetails>
                <AccordionActions>
                  <Box>
                    <Tooltip title="Show verification history">
                      <Button
                        startIcon={<ListAltIcon />}
                        color="info"
                        onClick={() => showMappingLogs(room, mappedRoom)}
                      >
                        Mapping Logs
                      </Button>
                    </Tooltip>
                  </Box>
                  <Box sx={{ flexGrow: 1 }} />
                  <Box ml="auto">
                    {mappedRoom.verificationStatus !== 'Approved' && (
                      <Tooltip title="Verify Room Mapping">
                        <Button
                          startIcon={<CheckCircleOutline />}
                          onClick={() => showConfirmVerifyRoomMapping(mappedRoom)}
                          variant="contained"
                          color="success"
                        >
                          Verify
                        </Button>
                      </Tooltip>
                    )}
                  </Box>
                </AccordionActions>
              </Accordion>
            ))}
          </Stack>
          {newMappings?.internalRoomIds?.length > 0 && (
            <>
              <Divider />
              <Stack direction="column" gap={1}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6" color="info.main">
                    New Mappings
                  </Typography>
                  <Button startIcon={<DeleteForever />} onClick={clearNewMappings} variant="contained" color="error">
                    Clear new Mappings
                  </Button>
                </Stack>
                <Stack direction="column">
                  {newMappings.internalRoomIds.map((internalRoomId) => (
                    <ToBeMappedRoomElement
                      key={`${internalRoomId}`}
                      internalRoomId={internalRoomId}
                      unsavedInternalRooms={unsavedInternalRooms}
                    />
                  ))}
                </Stack>
              </Stack>
            </>
          )}
        </Stack>
      </Stack>
      <Dialog open={isDeleteConfirmationModalOpen} onClose={() => setCloseDeleteConfirmationModal()}>
        {isDeleteConfirmationModalOpen && (
          <>
            <DialogTitle>Are you sure you want to delete this verified mapping?</DialogTitle>
            <Stack direction="row" justifyContent="space-between" p={3}>
              <Button onClick={() => setCloseDeleteConfirmationModal()} variant="outlined" color="primary">
                Cancel
              </Button>
              <Button onClick={confirmMappedRoomDelete} variant="contained" color="error">
                Confirm Delete
              </Button>
            </Stack>
          </>
        )}
      </Dialog>
      <Dialog open={isVerifyConfirmationModalOpen} onClose={() => setCloseVerifyConfirmationModal()}>
        {isVerifyConfirmationModalOpen && (
          <>
            <DialogTitle>Are you sure you want to verify this unverified mapping?</DialogTitle>
            <Stack direction="row" justifyContent="space-between" p={3}>
              <Button onClick={() => setCloseVerifyConfirmationModal()} variant="outlined" color="error">
                Cancel
              </Button>
              <Button onClick={confirmMappedRoomVerify} variant="contained" color="primary">
                Verify
              </Button>
            </Stack>
          </>
        )}
      </Dialog>
      {isMappingLogModalOpen && (
        <MappingLogModal
          isMappingLogModalOpen={isMappingLogModalOpen}
          setCloseMappingLogModal={setCloseMappingLogModal}
          logsTargetId={logsRoom.id}
          logsSourceId={mappingLogsRoom.internalRoomId}
          type="room"
        />
      )}
    </Paper>
  );
}
